import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CancelIcon from "@mui/icons-material/Cancel";
import ReplayIcon from "@mui/icons-material/Replay";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { MutationStepRow, MutationStepData } from "./MutationStepRow";

export interface MutationPlanDialogData {
  id: number;
  title: string;
  status: string;
  expiresAt: string;
  createdBy: number;
  steps: MutationStepData[];
}

interface MutationPlanDialogProps {
  open: boolean;
  onClose: () => void;
  plan: MutationPlanDialogData;
  onApprove?: (planId: number) => void;
  onCancel?: (planId: number) => void;
  onRetry?: (planId: number) => void;
  onAskAgentToFix?: (planId: number, errorMessage: string) => void;
  onStepInputChange?: (stepId: number, input: Record<string, any>) => void;
  loading?: boolean;
}

const STATUS_DISPLAY: Record<string, { label: string; color: "default" | "primary" | "success" | "error" | "warning" }> = {
  STAGED: { label: "Staged — Awaiting Review", color: "primary" },
  EXECUTING: { label: "Executing...", color: "warning" },
  COMPLETED: { label: "Completed", color: "success" },
  FAILED: { label: "Failed", color: "error" },
};

export const MutationPlanDialog: React.FC<MutationPlanDialogProps> = ({
  open,
  onClose,
  plan,
  onApprove,
  onCancel,
  onRetry,
  onAskAgentToFix,
  onStepInputChange,
  loading = false,
}) => {
  const statusInfo = STATUS_DISPLAY[plan.status] ?? { label: plan.status, color: "default" as const };
  const isEditable = plan.status === "STAGED" || plan.status === "FAILED";
  const canApprove = plan.status === "STAGED";
  const canCancel = plan.status === "STAGED";
  const canRetry = plan.status === "FAILED";

  const expiresIn = getTimeRemaining(plan.expiresAt);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { maxHeight: "80vh" }, "data-cy": "mutation-plan-dialog" } as any}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
            {plan.title}
          </Typography>
          <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
        </Box>
        {isEditable && expiresIn && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
            Expires {expiresIn}
          </Typography>
        )}
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {plan.steps.length} step{plan.steps.length !== 1 ? "s" : ""} in this plan.
          {isEditable && " Review the proposed changes below and approve when ready."}
        </Typography>

        {plan.steps
          .slice()
          .sort((a, b) => a.sequence - b.sequence)
          .map(step => (
            <MutationStepRow
              key={step.id}
              step={step}
              editable={isEditable}
              onInputChange={onStepInputChange}
            />
          ))}

        {plan.status === "COMPLETED" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 2 }}>
            <CheckCircleIcon fontSize="small" color="success" />
            <Typography variant="body2" color="success.main">
              All steps completed successfully
            </Typography>
          </Box>
        )}

        {plan.status === "FAILED" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 2 }}>
            <ErrorIcon fontSize="small" color="error" />
            <Typography variant="body2" color="error">
              Plan execution failed — review the error and retry when ready
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}

        <Box sx={{ flex: 1 }} />

        {canCancel && (
          <Button
            size="small"
            color="inherit"
            data-cy="mutation-plan-reject-btn"
            startIcon={<CancelIcon />}
            onClick={() => onCancel?.(plan.id)}
            disabled={loading}
          >
            Reject
          </Button>
        )}

        {canApprove && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            data-cy="mutation-plan-approve-btn"
            startIcon={<PlayArrowIcon />}
            onClick={() => onApprove?.(plan.id)}
            disabled={loading}
          >
            Approve & Execute
          </Button>
        )}

        {canRetry && onAskAgentToFix && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            data-cy="mutation-plan-dialog-ask-agent-btn"
            startIcon={<AutoFixHighIcon />}
            onClick={() => {
              const failedStep = plan.steps.find(s => s.status === "FAILED");
              const errorMsg = failedStep?.errorMessage ?? "Unknown error";
              onAskAgentToFix(plan.id, errorMsg);
              onClose();
            }}
            disabled={loading}
          >
            Ask Agent to Fix
          </Button>
        )}

        {canRetry && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            data-cy="mutation-plan-retry-btn"
            startIcon={<ReplayIcon />}
            onClick={() => onRetry?.(plan.id)}
            disabled={loading}
          >
            Retry Failed Steps
          </Button>
        )}

        {!canApprove && !canCancel && !canRetry && (
          <Button size="small" data-cy="mutation-plan-close-btn" onClick={onClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

function getTimeRemaining(expiresAt: string): string | null {
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "expired";
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 0) return `in ${hours}h ${minutes}m`;
  return `in ${minutes}m`;
}
